import { MainLayout } from '@/components/layouts/MainLayout'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Modal } from '@/components/ui/Modal'
import { Link, router, useForm } from '@inertiajs/react'
import { ArrowLeft, AlertTriangle } from 'lucide-react'
import { useState, type FormEvent } from 'react'

const DELETE_CONFIRMATION_PHRASE = 'SUPPRIMER'

interface UserData {
  id: number
  first_name: string
  last_name: string
  pseudo: string | null
  email: string
  rpps: string
  siret: string | null
  specialty: string
}

interface FormData {
  user: {
    first_name: string
    last_name: string
    pseudo: string
    specialty: string
  }
}

interface Props {
  user: UserData
  specialties: string[]
  is_pharmacien: boolean
  is_patient: boolean
}

export default function AccountEdit({ user, specialties, is_pharmacien, is_patient }: Props) {
  const { data, setData, patch, processing, errors } = useForm<FormData>({
    user: {
      first_name: user.first_name,
      last_name: user.last_name,
      pseudo: user.pseudo || '',
      specialty: user.specialty || ''
    }
  })

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const setUserData = (field: keyof FormData['user'], value: string) => {
    setData('user', { ...data.user, [field]: value })
  }

  const userErrors = errors as Record<string, string>

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    patch('/account')
  }

  function openDeleteModal() {
    setDeleteConfirmation('')
    setIsDeleteModalOpen(true)
  }

  function closeDeleteModal() {
    if (isDeleting) return
    setIsDeleteModalOpen(false)
  }

  function handleDeleteAccount() {
    if (deleteConfirmation !== DELETE_CONFIRMATION_PHRASE) return
    setIsDeleting(true)
    router.delete('/account', {
      onFinish: () => setIsDeleting(false)
    })
  }

  const canConfirmDeletion = deleteConfirmation === DELETE_CONFIRMATION_PHRASE && !isDeleting

  return (
    <MainLayout title="Modifier mon profil">
      <div>
        {/* Back link */}
        <Link
          href="/account"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-tessan-primary mb-6"
        >
          <ArrowLeft size={16} />
          Retour au profil
        </Link>

        <Card>
          <h1 className="text-2xl font-display font-bold text-gray-900 mb-6">
            Modifier mon profil
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Read-only fields */}
            <div className="space-y-4 pb-6 border-b border-gray-200">
              <h2 className="text-sm font-medium text-gray-500">
                Informations non modifiables
              </h2>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <p className="text-gray-900 bg-gray-50 px-4 py-2 rounded-lg">
                    {user.email}
                  </p>
                </div>
                {!is_patient && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {is_pharmacien ? 'SIRET' : 'RPPS'}
                    </label>
                    <p className="text-gray-900 bg-gray-50 px-4 py-2 rounded-lg font-mono">
                      {is_pharmacien ? user.siret : user.rpps}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Editable fields */}
            <div className="space-y-4">
              <h2 className="text-sm font-medium text-gray-500">
                Informations modifiables
              </h2>

              {/* Prénom et Nom sur la même ligne */}
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Prénom"
                  value={data.user.first_name}
                  onChange={(e) => setUserData('first_name', e.target.value)}
                  error={userErrors['user.first_name']}
                  required
                />
                <Input
                  label="Nom"
                  value={data.user.last_name}
                  onChange={(e) => setUserData('last_name', e.target.value)}
                  error={userErrors['user.last_name']}
                  required
                />
              </div>

              {/* Pseudo et Spécialité sur la même ligne */}
              <div className={`grid ${is_patient ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                <Input
                  label="Pseudo"
                  value={data.user.pseudo}
                  onChange={(e) => setUserData('pseudo', e.target.value.slice(0, 30))}
                  error={userErrors['user.pseudo']}
                  placeholder="Dr Dupont"
                  hint="Optionnel - 2-30 caractères"
                />
                {!is_patient && (
                  <Select
                    label="Spécialité"
                    options={specialties}
                    value={data.user.specialty}
                    onChange={(e) => setUserData('specialty', e.target.value)}
                    error={userErrors['user.specialty']}
                    placeholder="Sélectionnez votre spécialité"
                  />
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
              <Link href="/account">
                <Button type="button" variant="outline">
                  Annuler
                </Button>
              </Link>
              <Button type="submit" disabled={processing}>
                {processing ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>

            {/* Lien suppression de compte (RGPD) */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={openDeleteModal}
                className="text-sm text-gray-500 hover:text-gray-700 hover:underline focus:outline-none focus:underline"
              >
                Gérer mes données personnelles
              </button>
            </div>
          </form>
        </Card>

        <Modal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          title="Supprimer mon compte"
          size="md"
        >
          <div className="space-y-4">
            <div className="flex gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-medium mb-1 text-gray-900">Cette action est irréversible.</p>
                <p>
                  Votre compte sera supprimé et vous ne pourrez plus vous reconnecter.
                  Vos publications et commentaires resteront visibles dans la communauté
                  mais seront affichés de manière anonyme.
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-700">
              Conformément à la réglementation française (RGPD), vous avez le droit de
              demander la suppression de votre compte à tout moment.
            </p>

            <div>
              <label htmlFor="delete-confirmation" className="block text-sm font-medium text-gray-700 mb-1">
                Pour confirmer, saisissez{' '}
                <span className="font-mono font-semibold text-gray-900">
                  {DELETE_CONFIRMATION_PHRASE}
                </span>{' '}
                ci-dessous :
              </label>
              <Input
                id="delete-confirmation"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder={DELETE_CONFIRMATION_PHRASE}
                autoComplete="off"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Annuler
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={handleDeleteAccount}
                disabled={!canConfirmDeletion}
              >
                {isDeleting ? 'Suppression…' : 'Supprimer définitivement'}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </MainLayout>
  )
}
